import React from 'react';
import { X, DollarSign, Clock, AlertCircle, CheckCircle, MapPin, Calendar, FileText } from 'lucide-react';
import { Processor, Loan } from '../../types/dashboard';
interface ProcessorDetailModalProps {
  processor: Processor;
  loans: Loan[];
  onClose: () => void;
}
export function ProcessorDetailModal({
  processor,
  loans,
  onClose
}: ProcessorDetailModalProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'At Risk':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Delayed':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'Clear to Close':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Application':
        return 'text-blue-600';
      case 'Processing':
        return 'text-purple-600';
      case 'Underwriting':
        return 'text-amber-600';
      case 'Closing':
        return 'text-emerald-600';
      default:
        return 'text-slate-600';
    }
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-156">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-157">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white" data-id="element-158">
          <div className="flex justify-between items-start" data-id="element-159">
            <div className="flex items-center gap-4" data-id="element-160">
              <img src={processor.avatar} alt={processor.name} className="w-16 h-16 rounded-full border-4 border-white shadow-lg" data-id="element-161" />
              <div data-id="element-162">
                <h2 className="text-2xl font-bold" data-id="element-163">{processor.name}</h2>
                <p className="text-blue-100 text-sm" data-id="element-164">
                  {processor.specialty} Specialist
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm" data-id="element-165">
                  <span className="flex items-center gap-1" data-id="element-166">
                    <FileText className="w-4 h-4" data-id="element-167" />
                    {processor.activeLoans} Active Loans
                  </span>
                  <span className="flex items-center gap-1" data-id="element-168">
                    <Clock className="w-4 h-4" data-id="element-169" />
                    {processor.efficiency}% Efficiency
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${processor.status === 'active' ? 'bg-emerald-400 text-emerald-900' : 'bg-slate-400 text-slate-900'}`} data-id="element-170">
                    {processor.status}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors" data-id="element-171">
              <X className="w-6 h-6" data-id="element-172" />
            </button>
          </div>
        </div>

        {/* Loan List */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-173">
          <div className="mb-4 flex justify-between items-center" data-id="element-174">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-175">
              Active Loan Pipeline
            </h3>
            <div className="text-sm text-slate-500" data-id="element-176">
              Total Value:{' '}
              <span className="font-bold text-slate-900" data-id="element-177">
                $
                {loans.reduce((sum, loan) => sum + loan.loanAmount, 0).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-178">
            {loans.map(loan => <div key={loan.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group" data-id="element-179">
                <div className="flex justify-between items-start mb-3" data-id="element-180">
                  <div className="flex-1" data-id="element-181">
                    <div className="flex items-center gap-3 mb-2" data-id="element-182">
                      <span className="font-bold text-slate-900 text-lg" data-id="element-183">
                        {loan.id}
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-bold border ${getStatusColor(loan.status)}`} data-id="element-184">
                        {loan.status}
                      </span>
                      <span className={`text-sm font-medium ${getStageColor(loan.stage)}`} data-id="element-185">
                        {loan.stage}
                      </span>
                    </div>
                    <h4 className="font-semibold text-slate-700 mb-1" data-id="element-186">
                      {loan.borrowerName}
                    </h4>
                    <div className="flex items-center gap-1 text-sm text-slate-500" data-id="element-187">
                      <MapPin className="w-3 h-3" data-id="element-188" />
                      {loan.propertyAddress}
                    </div>
                  </div>

                  <div className="text-right" data-id="element-189">
                    <div className="text-2xl font-bold text-slate-900" data-id="element-190">
                      ${(loan.loanAmount / 1000).toFixed(0)}K
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-191">
                      {loan.loanType}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 pt-3 border-t border-slate-100" data-id="element-192">
                  <div data-id="element-193">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-194">
                      <Clock className="w-3 h-3" data-id="element-195" />
                      Days in Stage
                    </div>
                    <div className={`font-bold ${loan.daysInStage > 10 ? 'text-rose-600' : loan.daysInStage > 7 ? 'text-amber-600' : 'text-slate-900'}`} data-id="element-196">
                      {loan.daysInStage} days
                    </div>
                  </div>

                  <div data-id="element-197">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-198">
                      <Calendar className="w-3 h-3" data-id="element-199" />
                      Closing Date
                    </div>
                    <div className="font-medium text-slate-900 text-sm" data-id="element-200">
                      {loan.closingDate}
                    </div>
                  </div>

                  <div data-id="element-201">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-202">
                      <AlertCircle className="w-3 h-3" data-id="element-203" />
                      Conditions
                    </div>
                    <div className={`font-bold ${loan.pendingConditions > 3 ? 'text-rose-600' : loan.pendingConditions > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-204">
                      {loan.pendingConditions} pending
                    </div>
                  </div>

                  <div className="flex items-end justify-end" data-id="element-205">
                    <button className="text-xs text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors" data-id="element-206">
                      View Details →
                    </button>
                  </div>
                </div>
              </div>)}
          </div>

          {loans.length === 0 && <div className="text-center py-12 text-slate-400" data-id="element-207">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-208" />
              <p data-id="element-209">No active loans assigned to this processor</p>
            </div>}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-210">
          <div className="text-sm text-slate-600" data-id="element-211">
            Showing {loans.length} of {processor.activeLoans} loans
          </div>
          <button onClick={onClose} className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium" data-id="element-212">
            Close
          </button>
        </div>
      </div>
    </div>;
}